import React, { useState, useEffect } from "react"; // Import React and hooks
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import Select from "react-select"; // Import react-select
import { BASE_URL } from "../constants";
import { useAuth } from './../Context/AuthContext';

const TaskModal = ({ show, onHide, task, onSubmit, title, setToastMessage, setShowToast }) => {
  const [taskData, setTaskData] = useState(task || { taskName: "", description: "", dueDate: "", photo: "" });
  const [refugees, setRefugees] = useState([]); // State to store the list of refugees
  const [assignData, setAssignData] = useState({ refugeeId: "", groupId: "" });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const shelterId = user.shelter.id;

  useEffect(() => {
    if (show) {
      // Fetch refugees only when the modal is shown
      fetchRefugees();
    }

    if (task) {
      setTaskData(task); // Set task data for editing
    }
  }, [show, task]);

  // Fetch refugees from the API
  const fetchRefugees = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/refugees/shelter/${shelterId}`);
      setRefugees(response.data); 
    } catch (err) {
     
    }
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setTaskData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle refugee assignment changes
  const handleAssignChange = (selectedOptions) => {
    if (selectedOptions) {
      const selectedRefugees = selectedOptions.map(option => option.value);
      setAssignData({ ...assignData, refugeeId: selectedRefugees, groupId: "" });
    } else {
      setAssignData({ ...assignData, refugeeId: [], groupId: "" });
    }
  };
  const handleSubmit = async () => {
    if (!taskData.taskName || !shelterId) {
      setError("Please fill out all required fields.");
      return;
    }
  
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");

      const dueDateValue = taskData.dueDate ? taskData.dueDate : null;

      const assignedToValue = assignData.refugeeId || assignData.groupId || null;
  
      const payload = {
        taskName: taskData.taskName,
        description: taskData.description,
        dueDate: dueDateValue,
        photo: taskData.photo,
        assignedTo: assignedToValue,
        shelter: shelterId,
      };
  
      let response;
  
      if (taskData._id) {
        
        response = await axios.put(
          `${BASE_URL}/tasks/${taskData._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
       
        response = await axios.post(
          `${BASE_URL}/tasks`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
  
      if (setToastMessage && setShowToast) {
        const message = taskData._id ? "Task successfully updated!" : "Task successfully added!";
        setToastMessage(message);
        setShowToast(true);
      }
      
      setError("");
  
      if (onSubmit) {
        onSubmit(response.data);
      }
  
      // Reset form fields
      setTaskData({
        taskName: "",
        description: "",
        dueDate: "",
        photo: "",
      });
      setAssignData({
        refugeeId: [],
        groupId: null,
      });
  
      setTimeout(() => {
        setSuccessMessage("");
        onHide(); // Close modal
      }, 2000);
  
    } catch (err) {
      console.error("Task submission failed:", err);
  
      if (err.response) {
        console.error("Server responded with:", err.response.data);
        console.error("Status code:", err.response.status);
        setError(err.response.data.message || "Failed to save task. Please try again.");
      } else if (err.request) {
        console.error("No response received:", err.request);
        setError("No response from server. Please check your network.");
      } else {
        console.error("Error setting up the request:", err.message);
        setError("Unexpected error occurred. Please try again.");
      }
  
      setSuccessMessage(""); // Clear success message
  
    } finally {
      setIsLoading(false);
    }
  };
  
  
  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="taskName">
            <Form.Label>Task Name *</Form.Label>
            <Form.Control
              type="text"
              name="taskName"
              placeholder="Enter task name"
              value={taskData.taskName}
              onChange={handleChange}
              required
            />
            {!taskData.taskName && <div className="invalid-feedback">Task Name is required.</div>}
          </Form.Group>

          <Form.Group controlId="description" className="mt-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              placeholder="Enter task description"
              value={taskData.description}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group controlId="dueDate" className="mt-3">
            <Form.Label>Due Date</Form.Label>
  <Form.Control
    type="date"
    name="dueDate"
    value={taskData.dueDate}
    onChange={handleChange}
    min={new Date().toISOString().split("T")[0]} 
  />
</Form.Group>
          {/* Dropdown for selecting refugee(s) using react-select */}
          <Form.Group controlId="assignRefugeeId" className="mt-3">
  <Form.Label>Assign to Refugee(s)</Form.Label>
  <Select
    isMulti
    name="refugeeId"
    options={refugees.map((refugee) => ({
      value: refugee._id,
      label: refugee.firstName && refugee.lastName
        ? `${refugee.firstName} ${refugee.lastName}`
        : refugee.identifiant,
    }))}
    value={refugees
      .filter((refugee) => assignData.refugeeId.includes(refugee._id))
      .map((refugee) => ({
        value: refugee._id,
        label: refugee.firstName && refugee.lastName
          ? `${refugee.firstName} ${refugee.lastName}`
          : refugee.identifiant,
      }))}
    onChange={handleAssignChange}
    placeholder="Select Refugees"
  />
</Form.Group>


        </Form>

        <div className="mt-3">
          {error && <Alert variant="danger">{error}</Alert>}

        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? <Spinner animation="border" size="sm" /> : (taskData._id ? "Update Task" : "Add Task")}
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TaskModal;
